import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { ProductSlug, SkuOption } from "@/api/product-api";
import { Form } from "@/v2-components/ui/form";
import type { SelectOptionFormData } from "@/v2-components/cart-sheet/types";
import { TactGloveOptions } from "@/v2-components/cart-sheet/TactGloveOptions";
import { ColorOptions } from "@/v2-components/cart-sheet/ColorOptions";
import { cn } from "@/libs/utils";
import { RAIL_SECTION_GAP } from "@/components/tactsuit/components/buy-tactsuit/rail-accordion";
import { CartUtil } from "@/utils/CartUtil";
import { getAvailableColors } from "@/utils/product-colors";
import { useTrans } from "@/utils/useTrans";
import { NotifyMe } from "@/v2-components/pages/tactsuit/NotifyMe";

export function ProductOptionSelect({
  selectedOptions = [],
  updateSelectedOption,
  skuOptions,
  productSlug,
}: {
  productSlug: ProductSlug;
  skuOptions: SkuOption[];
  selectedOptions?: { type: string; value: string }[];
  updateSelectedOption: (option: { type: string; value: string }) => void;
}) {
  const { t } = useTrans(["gnb"]);
  const options = useMemo(
    () =>
      selectedOptions.reduce(
        (acc, { type, value }) => {
          acc[type] = value;
          return acc;
        },
        {} as Record<string, string>,
      ),
    [selectedOptions],
  );

  const form = useForm<SelectOptionFormData>({
    defaultValues: { options },
  });

  // ★스토어에 **없는** 옵션 키는 빈 값으로 명시해 reset 한다.
  //
  // react-hook-form 의 `reset` 은 값에서 **빠진 키**의 Controller 값을 지우지 않는다
  // (2026-10-15 jest 실측: `{color, size:"large"}` → `reset({options:{color}})` 뒤에도
  // `options.size` = `"large"`). 그래서 스토어가 사이즈를 지워도 칩은 선택된 채 남고,
  // `getSku()` 는 스토어를 보므로 담기·결제가 「사이즈 미선택」으로 스크롤만 했다.
  // prod 재현(KR): DK3 L → 결제하기 → 결제 페이지에서 뒤로 가기 → 장바구니 담기.
  // 뒤로 오면 이 폼이 **이전 방문의 스토어 값(L)** 으로 초기화되고, 직후 페이지 마운트 효과가
  // 스토어를 `reset()` 해 색상만 남긴다 — 그 부분 값 reset 이 L 을 못 지웠다.
  useEffect(() => {
    const current = form.getValues("options") ?? {};
    const cleared = Object.fromEntries(
      Object.keys(current).map((type) => [type, ""]),
    );
    form.reset({ options: { ...cleared, ...options } });
  }, [form, options]);

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!name?.startsWith("options.")) return;

      const changedType = name.replace("options.", "");
      const changedValue = value.options?.[changedType];

      // 글러브 같은 경우 색상변경시 사이즈가 초기화 되므로
      // changedValue === "" 일수 있다.
      updateSelectedOption({ type: changedType, value: changedValue });
    });

    return () => subscription.unsubscribe();
  }, [form, updateSelectedOption]);

  if (!skuOptions.length) {
    return null;
  }

  /**
   * 품절 색 알림 — 글러브의 「Not available in your size?」 박스(`TactGloveOptions`)의 색상판 (#5922).
   *
   * 색 스와치는 품절 색을 `disabled` 로 막는다(고르면 담기가 품절 SKU 로 간다). 그래서 일부 색만 품절인
   * 배지 PDP(예: `10th-birthday-badge-for-tactsuit-air`)에서는 그 색의 재입고 알림을 걸 길이 없었다 —
   * 하단 CTA 의 Notify me 는 **제품 전체가 품절**일 때만 나온다. 일부 색만 품절일 때 이 박스를 띄운다
   * (전부 품절이면 CTA 가 이미 Notify me 라 중복이라 안 띄운다). 모달엔 첫 품절 색을 미리 채운다.
   */
  const colors = CartUtil.isGloveDevice(productSlug)
    ? []
    : getAvailableColors(skuOptions);
  const soldOutColor = colors.some((c) => c.available)
    ? colors.find((c) => !c.available)?.color
    : undefined;

  // 글러브(DK2/DK3)는 동일 컴포넌트 — productSlug 로 분기되므로 caseBy 케이스별 중복 불필요.
  const gloveOptions = (
    <TactGloveOptions
      skuOptions={skuOptions}
      productSlug={productSlug}
      showErrorWrapper
      railLayout
    />
  );

  return (
    <Form {...form}>
      {/* cart-sheet 에서 가져온 `-mx-4 px-4 laptop:-mx-8 laptop:px-8 flex-1
          overflow-y-auto` 를 걷어냈다. 시트(고정 높이 + 풀블리드) 전용 값이라
          구매 레일에서는 실제로 해가 됐다:
          - `overflow-y-auto` 는 `overflow-x` 까지 auto 로 만든다(한 축이 visible 이
            아니면 다른 축의 visible 은 auto 로 계산됨) → 스크롤 컨테이너가 되어
            에러 하이라이트 같은 음수 오프셋 배경이 잘릴 수 있다.
          - `laptop:-mx-8`(32px)이 레일-이미지열 갭(24px)보다 커서 form 박스가
            **좌측 이미지를 8px 덮었다** — 그 지점 클릭이 form 에 먹혔다.
          음수 마진 없이도 콘텐츠는 이미 레일 x=0 에 정렬된다. */}
      <form className="flex flex-col">
        <section>
          {/* 간격은 여기 gap 하나로만 만든다(자식 mb 는 railLayout 으로 끔). */}
          <div className={cn("flex flex-col", RAIL_SECTION_GAP)}>
            {/* ⚠️ 슬러그를 여기서 다시 열거하지 않는다. "사이즈가 있는 글러브인가"는
                `CartUtil.isGloveDevice` 가 단일 출처이고, 이 브랜치의 액세서리 필터
                (`ProductUtil.getOfferedAccessories`)도 그걸 쓴다. 여기만 하드코딩하면
                새 글러브(DK4)가 액세서리는 맞게 걸러지는데 **사이즈 선택 UI 가 안 떠서
                `getSku()` 가 null 이 되고 구매 자체가 막힌다** — #4921 이 DK2 하드코딩을
                일반화하며 고쳤던 그 실패를 한 층 위에서 재현하게 된다. */}
            {CartUtil.isGloveDevice(productSlug) ? (
              gloveOptions
            ) : (
              <ColorOptions
                skuOptions={skuOptions}
                showErrorWrapper
                imageSwatch
                railLayout
              />
            )}
            {soldOutColor && (
              <div className="flex items-center justify-between rounded-lg bg-coolgray-10 p-5">
                <p className="text-base tracking-[0.25px] text-coolgray-90">
                  {t("Not available in your color?")}
                </p>
                <NotifyMe
                  productSlug={productSlug}
                  initialColor={soldOutColor}
                  trigger={
                    <button
                      type="button"
                      className="text-base font-500 tracking-[0.1px] text-blue-60 underline"
                    >
                      {t("Notify me")}
                    </button>
                  }
                />
              </div>
            )}
          </div>
        </section>
      </form>
    </Form>
  );
}
